import React, { useEffect, useState } from "react";
import Select from "react-select";
import { MDBBtn, MDBCard, MDBCardBody, MDBCardHeader, MDBCardTitle, MDBCheckbox, MDBIcon, MDBInput, MDBSpinner } from "mdb-react-ui-kit";
import Moment from "react-moment";
import ApiService from "../../Core/Service/ApiService";
import AdminTable from "../../Components/AdminTable/AdminTable";
import { useNavigate, useParams } from "react-router-dom";
import LoadingIndicator from "../../Components/LoadingIndicator/LoadingIndicator";
import BulkActionPanel, { BulkActions } from "../../Components/CMS/BulkActionPanel";
import { LondonDate } from "../../Utils/utils";
import Paginator from "../../Components/Paginator/Paginator";
import { CmsType, CmsTypeStr, ChatbotCmsTypes } from "../../Constants/constants";
import { useContext } from "react";
import CmsContext from "../../Context/CmsContext";

const CmsItemList = React.memo((props) => {
    const params = useParams();
    const {cms_id} = params;
    const cmsContext = useContext(CmsContext);
    const {currMainMenu, currSubMenu} = cmsContext;
    const navigate = useNavigate();
    const [total, setTotal] = useState(0);
    const [cms, setCms] = useState();
    const [rows, setRows] = useState([]);
    const perPageOptions = [
        {value: 10, label: '10'},
        {value: 20, label: '20'},
        {value: 50, label: '50'},
        {value: 100, label: '100'},
    ]
    const [formData, setFormData] = useState({
        cms_id,
        pageNum: 1,
        perPage: 10,
        search: {},
        sort: {
            name: 'sequence',
            dir: 'ASC'
        }
    })
    const [loading, setLoading] = useState(false);
    const [bulkActioning, setBulkActioning] = useState(false);

    useEffect(() => {
        getRows();
    }, [formData]);

    useEffect(() => {
        cmsContext.setBreadcrumb([
            {title: currMainMenu?.label ?? ''},
            {title: currSubMenu?.label ?? ''},
            {title: 'Items'},
            {title: 'List'},
        ]);
    }, [currMainMenu, currSubMenu])

    const onAllItemSelected = (selected) => {
        setRows(rows.map(row => ({
            ...row,
            selected: selected
        })))
    }
    const onPageChanged = (pageNum) => {
        setFormData({
            ...formData,
            pageNum
        })
    }
    const onPerPageChanged = (opt) => {
        setFormData({
            ...formData,
            pageNum: 1,
            perPage: opt.value
        })
    }
    const handleSearch = (search) => {
        setFormData({
            ...formData,
            pageNum: 1,
            search: search
        })
    }
    const handleSort = (sort) => {
        setFormData({
            ...formData,
            pageNum: 1,
            sort: sort
        })
    }
    const getRows = () => {
        if(!cms_id) return;

        setLoading(true); 
        const data = {
            ...formData,
            search: JSON.stringify(formData.search),
            sort: JSON.stringify(formData.sort)
        }
        ApiService.get(`/cms_items`, data).then((response)=>{
            const curr_rows = [...rows];

            setCms(response.data.cms);
            setTotal(response.data.total);
            setRows(response.data.rows?.map((row, index) => {
                const old_row = curr_rows?.find(r => r.id == row.id);
                return {
                    ...row,
                    data: row.data ? JSON.parse(row.data) : {},
                    selected: old_row?.selected ?? false,
                }
            }));
        })
        .finally(() => {
            setLoading(false);
        });
    }

    const onActiveChange = (e, row) => {
        const data = {
            active: e.target.checked
        }

        const message = e.target.checked ? "Are you sure to activate?" : "Are you sure to deactivate?";
        if(window.confirm(message) == false) return;
        
        ApiService.put(`/cms_items/${row.id}/activate`, data).then(() => {
            getRows();
        })
    }
    const onAddBtnClicked = (e) => {
        navigate(`/cms_item/${cms_id}/add?type=${cms.type}`);
    }
    const gotoEdit = (row) => {
        navigate(`/cms_item/${cms_id}/edit/${row.id}?type=${cms.type}`);
    }
    const handleCopy = (row) => {
        navigate(`/cms_item/${cms_id}/copy/${row.id}?type=${cms.type}`);
    }

    const handleDelete = (row) => {
        if(window.confirm("Are you sure to delete?") == false) return;
        ApiService.delete(`/cms_items/${row.id}`).then(() => {
            getRows();
        })
    }
    const onRowSelected = (e, row) => {
        setRows(rows.map(r => {
            if(r.id != row.id) return r;
            return {
                ...r,
                selected: e.target.checked
            }
        }))
    }
    const handleBulkAction = (bulkData) => {
        if(!bulkData.action) return;
        const selRows = rows?.filter(row => row.selected);
        if(selRows?.length > 0) {
            if(window.confirm('Are you sure to do this action?') == false) return;
            
            setBulkActioning(true);
            const data = {
                ...bulkData,
                ids: selRows.map(r => r.id)
            }
            ApiService.post(`/cms_items/bulkaction`, data).then(() => {
                getRows();
            })
            .finally(() => {
                setBulkActioning(false);
            })
        }
    }
    const moveUpDown = (row, dir) => {
        ApiService.get(`/cms/move/${row.id}/${dir}`).then(() => {
            getRows();
        })
    }
    const gotoCmsList = () => {
        navigate(`/cms/${cms.type}/list`);
    }
    const gotoCmsEdit = () => {
        navigate(`/cms/${cms?.type}/edit/${cms?.id}`);
    }

    const renderCmsInfo = () => {
        return (
        <MDBCard>
            <MDBCardHeader>
                <MDBCardTitle className="d-flex align-items-center">
                    <div className="flex-fluid">CMS Main Info</div>
                    <div>
                        <MDBBtn className="" onClick={gotoCmsEdit}>Edit</MDBBtn>
                    </div>
                </MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody>
                <div className="cms-info d-flex align-items-center">
                    <div>
                        <label className="comment">Type: </label>
                        <div className="">{ CmsTypeStr[cms?.type] }</div>
                    </div>
                    <div className="ms-4">
                        <label className="comment">Page Position: </label>
                        <div className="">{ cms?.page_pos }</div>
                    </div>
                    <div className="ms-4">
                        <label className="comment">Start Date: </label>
                        <div className=""><Moment format="YYYY-MM-DD">{ LondonDate(cms?.start_date) }</Moment></div>
                    </div>
                    <div className="ms-4">
                        <label className="comment">End Date: </label>
                        <div className=""><Moment format="YYYY-MM-DD">{ LondonDate(cms?.end_date) }</Moment></div>
                    </div>
                </div>
            </MDBCardBody>
        </MDBCard>
        )
    }

    const renderItems = () => {
        return (
        <MDBCard className="mt-4">
            <MDBCardHeader>
                <MDBCardTitle>Items</MDBCardTitle>
            </MDBCardHeader>
            <MDBCardBody className="d-flex flex-column h-100">
                <div className="d-flex align-items-center me-1 mt-2">
                    <div className="flex-fluid d-flex align-items-center ps-2">
                        <label>Per Page:</label>
                        <div className="ms-2" style={{width:100}}>
                            <Select 
                                options={perPageOptions} 
                                value={perPageOptions.find(opt=>opt.value == formData.perPage)} 
                                onChange={onPerPageChanged}
                            />
                        </div>
                        <label className="ms-4">
                            Found 
                            <span className='fw-bold mx-2' style={{color: '#333'}}>{total}</span> 
                            Matching Results.
                        </label>
                    </div>

                    <div className="d-flex align-items-center">
                        <BulkActionPanel 
                            bulkActions={[
                                BulkActions.Activate,
                                BulkActions.Deactivate,
                                BulkActions.Delete,
                            ]}
                            handleBulkAction={handleBulkAction} 
                            bulkActioning={bulkActioning} 
                        />
                        <MDBBtn className="ms-4" onClick={onAddBtnClicked}>
                            <MDBIcon icon="plus" />
                            <span className="ms-1">Create New Content</span>
                        </MDBBtn>
                    </div>
                </div>

                <div className="flex-fluid mt-3">
                    <AdminTable 
                        columns={columns} 
                        rows={rows} 
                        renderTbodyTr={renderTbodyTr} 
                        handleSearch={handleSearch} 
                        handleSort={handleSort}
                        search={formData.search} 
                        sort={formData.sort}
                    />
                </div>

                <div className="flex-fluid d-flex justify-content-center mt-4">
                    <Paginator curPage={formData.pageNum} total={total} perPage={formData.perPage} onPageChanged={onPageChanged} />
                </div>
            </MDBCardBody>
        </MDBCard>
        )
    }

    const renderTbodyTr = (row, rowIndex) => {
        const now = LondonDate(new Date());
        let className = "cursor-pointer ";

        if(row.active == "1") {
            className += "active";
        } else {
            className += "inactive";
        }

        return (
            <tr key={rowIndex} className={`${className}`}>
                { columns.map((col, colIndex) => {
                    const el = renderTdCell(row, col.data ?? col.name, rowIndex, colIndex);
                    if(el) {
                        return <td key={colIndex}>{el}</td>
                    } 
                    // else {
                    //     return <td key={colIndex}>{row[col.name]}</td>
                    // }
                })}
            </tr>
        )
    }

    const renderTdCell = (row, colname, rowIndex, colIndex) => {
        switch(colname) {
            case 'select':
                return <div className="d-flex align-items-center"> 
                    <MDBCheckbox name='' checked={row.selected ?? false} onChange={e => onRowSelected(e, row)} />
                    <span className="mt-1">{row.id}</span>
                </div>
            case 'link_url':
                if(row.link_url) {
                    return <a>{row.link_url}</a>;
                } else {
                    return <></>
                }
            case 'brand':
                return row['data']['brand'];
            case 'question':
                return row['data']?.['question'];
            case 'active':
                return <MDBCheckbox checked={row.active == "1"} onChange={e=>onActiveChange(e, row)} />
            case 'action':
                return <div className="d-flex justify-content-end">
                <MDBBtn color="none" tag="a" onClick={() => gotoEdit(row)} title="Edit Content">
                    <MDBIcon fas icon="pencil-alt" size="lg" color="warning" />
                </MDBBtn>
                <MDBBtn color="none" tag="a" title="Copy Content" onClick={() => handleCopy(row)}>
                    <MDBIcon far icon='copy' size="lg" className="ms-2" color="warning" />
                </MDBBtn>
                <MDBBtn color="none" tag="a" onClick={()=>handleDelete(row)} title="Delete Content">
                    <MDBIcon far icon="trash-alt" size="lg" color="danger" className="ms-2" />
                </MDBBtn>
            </div>
            default:
                return <div className="" onClick={e=>gotoEdit(row)}>{row[colname]}</div>;
        }        
    }

    const columns = [
        {name: 'select', title: 'ID', headerStyle:{width:50, paddingLeft:30}, search: {
            enable: true, type: 'check', onChange: onAllItemSelected
        }},
        {name: 'brand', title: 'Brand', sortable: true, 
            search: {enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]},
            disable: ![CmsType.Brand].includes(cms?.type),
        },
        {name: 'internal_name', title: 'Internal Name', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'ga_id', title: 'GA Id', headerStyle: {width: 100}, sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'question', title: 'Question', disable: !ChatbotCmsTypes.includes(cms?.type)},
        {name: 'link_url', title: 'Link Url', search: {
            enable: true, type: 'text', ops:[{value: 'like', label: 'Like'}]
        }},
        {name: 'prod_codes', title: 'Prod Codes', search: {
            enable: true, type: 'text', ops:[{value: 'like', label: 'Like'}]
        }, disable: ChatbotCmsTypes.includes(cms?.type)},
        {name: 'sequence', title: 'Sequence', headerStyle:{width:80}, sortable: true},
        {name: 'epos_managers.username', data: 'editor_username', title: 'Editor', sortable: true, search: {
            enable: true, type: 'text', ops: [{value: 'like', label: 'Like'}]
        }},
        {name: 'active', title: 'Active', headerStyle:{width:100}, sortable: true, search: {
            enable: true, type: 'select', ops:[{value:'=', label: '='}], options:[{value: '', label: 'All'}, {value: '1', label: 'Active'}, {value: '0', label: 'Deactive'}]
        }},
        {name: 'action', title: 'Action', headerClassName: ""}
    ];

    return (<>
        { renderCmsInfo() }

        { renderItems() }

        <div className="d-flex justify-content-end mt-4">
            <MDBBtn color="default" onClick={gotoCmsList}>Go To CMS List</MDBBtn>
        </div>

        <LoadingIndicator show={loading} />
    </>)
})

export default CmsItemList;